// Contenido Teórico Especializado - Redacción Indirecta EXANI-II (Ceneval 2026)
// Elaborado con estándar pedagógico docente especialista en reactivos de admisión.

export const REDACCION_INDIRECTA_THEORY = {
  ri_301: {
    title: "Acentuación Gráfica: Reglas Generales y Acento Diacrítico",
    intro: "En Redacción Indirecta, Ceneval no te pide redactar desde cero, sino evaluar oraciones y párrafos ya escritos para seleccionar la opción ortográficamente impecable.",
    sections: [
      {
        subtitle: "1. Clasificación por Sílaba Tónica (SEGA)",
        content: `- **Sobreesdrújulas**: El acento recae antes de la antepenúltima sílaba. **Siempre llevan tilde** (ej. *cómpratelo, explícaselo*).
- **Esdrújulas**: Acento en la antepenúltima sílaba. **Siempre llevan tilde** (ej. *médico, síntesis, exámenes*).
- **Graves (Llanas)**: Acento en la penúltima sílaba. Llevan tilde cuando **NO terminan en N, S o Vocal** (ej. *árbol, lápiz, cóndor*, pero *examen, resumen, crisis* sin tilde).
- **Agudas**: Acento en la última sílaba. Llevan tilde cuando **terminan en N, S o Vocal** (ej. *canción, compás, café*).`
      },
      {
        subtitle: "2. Acento Diacrítico en Monosílabos (¡Pregunta Segura!)",
        content: `Los monosílabos por regla general no se tildan, excepto los pares con valor diacrítico:
- **él** (pronombre) vs **el** (artículo): *Él compró el libro.*
- **tú** (pronombre) vs **tu** (posesivo): *Tú trajiste tu mochila.*
- **mí** (pronombre) vs **mi** (posesivo o nota musical): *Es para mí, mi amigo.*
- **té** (sustantivo bebida) vs **te** (pronombre): *¿Te sirvo una taza de té?*
- **más** (adverbio cantidad) vs **mas** (conjunción pero): *Quiero más tiempo, mas no puedo.*
- **sí** (afirmación o pronombre) vs **si** (condicional): *Sí, iré si no llueve.*
- **dé** (verbo dar) vs **de** (preposición): *Dígale que dé un vaso de agua.*
- **sé** (verbo ser o saber) vs **se** (pronombre reflexivo): *Sé valiente, ya sé lo que pasó.*`
      },
      {
        subtitle: "3. Hiatos Acentuales (Vocal Abierta + Cerrada Tónica)",
        content: `Cuando una vocal cerrada ($i, u$) es tónica junto a una abierta ($a, e, o$), se rompe el diptongo y **siempre lleva tilde**, desafiando las reglas generales:
*ma-íz, pa-ís, ba-úl, rí-o, ra-íz, le-í-do*.`
      }
    ],
    examples: [
      {
        problem: "¿Cuál oración está correctamente acentuada según la RAE?",
        solution: `'Él me pidió que le dé más café, mas se terminó temprano.'
Justificación:
- *Él* (pronombre con tilde).
- *dé* (verbo dar con tilde).
- *más* (cantidad con tilde).
- *mas* (sinónimo de pero, sin tilde).`
      }
    ],
    exam_tips: "Ojo: 'examen', 'resumen', 'joven' e 'imagen' NO llevan tilde porque son palabras graves terminadas en N. Al pluralizarse se vuelven esdrújulas y sí se tildan: 'exámenes', 'resúmenes', 'jóvenes', 'imágenes'."
  },

  ri_302: {
    title: "Signos de Puntuación: Punto, Coma, Punto y Coma, Dos Puntos",
    intro: "Mide la fluidez sintáctica y la eliminación de la 'coma criminal' entre sujeto y predicado.",
    sections: [
      {
        subtitle: "1. Usos Indispensables de la Coma",
        content: `- **Coma enumerativa**: Separa elementos análogos: *Trajo manzanas, peras, uvas y duraznos.*
- **Coma vocativa**: Aísla al interlocutor: *Estudiantes, abran su guía en la página diez.*
- **Coma explicativa (Incidental)**: Encierra aclaraciones que pueden suprimirse: *El director, quien asumió en marzo, presentó el balance.*
- **Coma hiperbática**: Tras alterar el orden sintáctico natural (Sujeto + Verbo + Complementos): *Bajo la intensa lluvia invernal, los soldados continuaron la marcha.*
- **Coma elíptica**: Sustituye a un verbo ya mencionado para evitar repeticiones: *Manuel estudia medicina; su hermana, derecho.*`
      },
      {
        subtitle: "2. ¡La Coma Criminal (Error Grave en EXANI-II)!",
        content: `**NUNCA** se debe colocar coma entre el sujeto y el verbo principal de la oración, por largo que sea el sujeto:
- *Incorrecto*: El rector de la universidad junto con el claustro docente, inauguró el ciclo.
- *Correcto*: El rector de la universidad junto con el claustro docente inauguró el ciclo.`
      },
      {
        subtitle: "3. El Punto y Coma (;)",
        content: `- Separa oraciones yuxtapuestas con estrecha relación de sentido: *Llovió toda la noche torrencialmente; los caminos quedaron intransitables.*
- Delante de conectores como *sin embargo, por lo tanto, no obstante* cuando la oración previa es extensa.`
      }
    ],
    examples: [
      {
        problem: "¿Cuál opción presenta puntuación correcta?",
        solution: `'Aunque el informe contenía severas discrepancias estadísticas, la junta directiva aprobó el financiamiento; no obstante, solicitó una auditoría externa.'`
      }
    ],
    exam_tips: "Si una oración tiene dos puntos (:), verifica que lo que sigue sea una enumeración anunciada, una causa-efecto o una cita textual directa."
  },

  ri_303: {
    title: "Grafías Dudosas y Homófonos Ortográficos",
    intro: "Evalúa la escritura precisa de homófonos que suenan idéntico pero cuyo significado cambia radicalmente con la letra empleada.",
    sections: [
      {
        subtitle: "1. Homófonos de Alta Frecuencia en Ceneval",
        content: `- **Haber** (verbo) vs **A ver** (preposición + verbo ver): *Debe haber una solución / Vamos a ver una película.*
- **Hay** (verbo haber) vs **Ahí** (lugar) vs **¡Ay!** (interjección de dolor): *Ahí hay una persona que gritó: ¡ay!*
- **Tubo** (cilindro hueco) vs **Tuvo** (verbo tener): *Tuvo que reparar el tubo de cobre.*
- **Echo** (del verbo echar, tirar) vs **Hecho** (del verbo hacer o suceso): *Yo echo la basura / El trabajo está bien hecho.*
- **Valla** (cerca) vs **Vaya** (verbo ir) vs **Baya** (fruto silvestre).
- **Cocer** (cocinar con calor) vs **Coser** (con aguja e hilo).`
      }
    ],
    examples: [
      {
        problem: "Completa la frase: 'El ingeniero ordenó que el personal ______ al laboratorio, pues no ______ suficiente tiempo.'",
        solution: `'vaya' (de ir con 'y') y 'había' (con 'h' y tilde en la 'i').`
      }
    ],
    exam_tips: "Desconfía de palabras inventadas que suenen sofisticadas. Los reactivos de Ceneval suelen poner palabras como 'conllevación' o 'aperturar' que son incorrectas en el registro formal."
  },

  ri_304: {
    title: "Concordancia Gramatical Nominal y Verbal",
    intro: "La concordancia es la correspondencia obligatoria de género y número entre sustantivos y adjetivos, y de persona y número entre sujeto y verbo.",
    sections: [
      {
        subtitle: "1. Concordancia Nominal (Sustantivo + Adjetivo)",
        content: `- Dos o más sustantivos de distinto género coordinados en singular concuerdan con el adjetivo en **masculino plural**: *La carpeta y el libro están nuev**os**.*
- Adjetivo que precede a varios sustantivos concuerda con el más próximo: *Su extraordinaria valentía y temple.*`
      },
      {
        subtitle: "2. Concordancia Verbal (Sujeto Complejo + Verbo)",
        content: `- **Sujetos colectivos con complemento plural**: Sustantivos como *mayoría, grupo, infinidad, conjunto* seguidos de 'de + sustantivo plural' admiten concordancia en singular (concordancia gramatical estricta, preferida por la RAE) o en plural (concordancia ad sensum):
  *La mayoría de los aspirantes aprobó el examen.*
- **Verbo 'Haber' impersonal**: Cuando 'haber' indica existencia de objetos o personas, es impersonal y **solo se conjuga en tercera persona singular**:
  - *Incorrecto*: Hubieron muchos heridos / Habían tres problemas.
  - *Correcto*: **Hubo** muchos heridos / **Había** tres problemas.`
      }
    ],
    examples: [
      {
        problem: "¿Cuál enunciado mantiene concordancia verbal correcta?",
        solution: `'Durante la ceremonia hubo múltiples reconocimientos para el equipo.' (Nunca 'hubieron').`
      }
    ],
    exam_tips: "Ceneval adora poner el verbo 'haber' en plural ('habían personas', 'hubieron quejas'). Es un distractor 100% incorrecto."
  },

  ri_305: {
    title: "Cohesión Textual y Uso de Conectores Lógicos",
    intro: "Mide cómo se articulan las ideas dentro de un párrafo para evitar saltos bruscos o relaciones lógicas erróneas.",
    sections: [
      {
        subtitle: "1. Catálogo de Conectores por Relación Semántica",
        content: `- **Causa**: *porque, ya que, puesto que, debido a que, dado que.*
- **Consecuencia**: *por lo tanto, por consiguiente, en consecuencia, de modo que.*
- **Oposición/Adversativos**: *sin embargo, no obstante, a pesar de que, por el contrario, en cambio.*
- **Adición**: *además, asimismo, por añadidura, igualmente.*
- **Condición**: *siempre que, a condición de que, con tal de que.*`
      }
    ],
    examples: [
      {
        problem: "Completa el fragmento: 'El equipo médico diseñó un protocolo riguroso; ________, los suministros no llegaron a tiempo, ________ la intervención debió posponerse.'",
        solution: `Primer espacio (contraste): **sin embargo** o **no obstante**.
Segundo espacio (consecuencia): **por lo tanto** o **de modo que**.`
      }
    ],
    exam_tips: "Si el conector indica oposición, la segunda idea debe contradecir o limitar a la primera. No uses 'sin embargo' donde corresponde 'por lo tanto'."
  },

  ri_306: {
    title: "Vicios del Lenguaje: Ambigüedad, Pleonasmo, Queísmo y Dequeísmo",
    intro: "Evalúa la habilidad para depurar textos con construcciones confusas o redundancias innecesarias.",
    sections: [
      {
        subtitle: "1. Dequeísmo vs Queísmo (El Truco Infalible)",
        content: `- **Dequeísmo**: Usar indebidamente 'de que' cuando solo corresponde 'que':
  - *Incorrecto*: Pienso de que deberíamos salir.
  - *Correcto*: Pienso que deberíamos salir.
- **Queísmo**: Omitir la preposición 'de' cuando el verbo la exige obligatoriamente:
  - *Incorrecto*: Me alegro que estés aquí.
  - *Correcto*: Me alegro de que estés aquí.
- **El Test de la Pregunta**: Convierte la frase en pregunta:
  - ¿Qué piensas? $\\rightarrow$ Pienso **que**...
  - ¿**De qué** te alegras? $\\rightarrow$ Me alegro **de que**...`
      },
      {
        subtitle: "2. Pleonasmos y Redundancias Inútiles",
        content: `Frases viciosas que Ceneval penaliza: *'subir para arriba', 'lapso de tiempo', 'hemorragia de sangre', 'erario público', 'volver a repetir', 'persona humana'.*`
      }
    ],
    examples: [
      {
        problem: "¿Cuál opción NO presenta dequeísmo ni queísmo?",
        solution: `'Estoy convencido de que la investigación aportará datos concluyentes.' (¿De qué estás convencido? $\\rightarrow$ de que...).`
      }
    ],
    exam_tips: "Verbos de pensamiento y habla (*pensar, opinar, creer, decir, afirmar*) NUNCA llevan 'de que'."
  },

  ri_307: {
    title: "Estructura Oracional: Sujeto, Predicado y Modificadores",
    intro: "Evalúa el orden lógico del enunciado en español (SVO: Sujeto + Verbo + Objeto) para evitar anfibologías y oraciones fragmentadas.",
    sections: [
      {
        subtitle: "1. El Núcleo de la Oración",
        content: `- **Sujeto**: De quien se afirma o niega algo. Su núcleo siempre es un sustantivo o pronombre.
- **Predicado**: Lo que se dice del sujeto. Su núcleo es el verbo conjugado.
- **Modificadores del Sujeto**: Modificador Directo (artículos, adjetivos) e Indirecto (preposición + término: *el libro de física*).
- **Complementos del Verbo**: Objeto Directo (¿qué?), Objeto Indirecto (¿a quién/para quién?), Complemento Circunstancial (tiempo, modo, lugar).`
      }
    ],
    examples: [
      {
        problem: "En la oración 'La profesora entregó las calificaciones a los alumnos en el aula', ¿cuál es el Objeto Directo?",
        solution: `'las calificaciones' (responde a la pregunta ¿qué entregó?).`
      }
    ],
    exam_tips: "Cuidado con los sujetos tácitos o implícitos: en 'Llegamos a tiempo al puerto', el sujeto tácito es 'Nosotros'."
  },

  ri_308: {
    title: "Registro Lingüístico Formal y Adecuación Académica",
    intro: "En el EXANI-II se evalúa la capacidad de seleccionar textos con léxico preciso, formal y académico, evitando vulgarismos, coloquialismos y muletillas.",
    sections: [
      {
        subtitle: "1. Rasgos del Registro Académico Formal",
        content: `- Uso de vocabulario preciso y unívoco (*efectuar, desarrollar, exponer* en lugar de *hacer, poner, decir*).
- Supresión de expresiones informales (*chido, un montón, bronca, agarrar la onda*).
- Objetividad y tono impersonal (*se comprobó experimentalmente* en vez de *vimos que*).`
      }
    ],
    examples: [
      {
        problem: "¿Cuál enunciado es el más adecuado para una carta formal de solicitud de beca universitaria?",
        solution: `'Por medio de la presente, solicito formalmente se considere mi postulación para el programa de estímulos académicos 2026.'`
      }
    ],
    exam_tips: "Descarta siempre opciones que utilicen modismos locales, diminutivos afectivos ('momentito') o signos de exclamación excesivos."
  },

  ri_309: {
    title: "Corrección de Estilo, Paráfrasis y Reescritura Eficaz",
    intro: "Reactivos de cierre: se presenta un párrafo confuso y debes elegir la opción que reescribe la misma idea con claridad, elegancia y brevedad.",
    sections: [
      {
        subtitle: "1. Criterios de Calidad en la Reescritura",
        content: `1. **Claridad**: Que el mensaje se entienda en la primera lectura sin ambigüedades.
2. **Concisión**: Eliminar palabras vacías que no aporten valor semántico.
3. **Fidelidad**: Conservar intacta la idea original sin distorsionarla ni inventar datos.`
      }
    ],
    examples: [
      {
        problem: "¿Cuál opción parafrasea mejor: 'A causa de que las condiciones meteorológicas fueron de extrema adversidad, el buque no pudo zarpar del muelle'?",
        solution: `'Debido al mal tiempo, el buque no pudo zarpar.'`
      }
    ],
    exam_tips: "La mejor opción casi siempre es la más clara y concisa; la brevedad con precisión es la virtud suprema en la redacción formal."
  }
};
